/**
 * screens/modals/SettleModal
 * Settle a leaver's open balance, or waive it with a reason.
 *
 * Settling is the normal path: the cash comes back, or the balance is deducted
 * through Payroll. Waiving writes it off, and — like every write-off in this
 * product — requires a reason that stays on the record.
 */
import { useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { ChipRow, Modal, Textarea } from '../../components';
import { neutral } from '../../theme/colors';
import { radii } from '../../theme/radii';
import { sans, size as fontSize, leading } from '../../theme/typography';
import { date } from '../../lib/dates';
import { format } from '../../lib/money';
import { resolveSettlement } from '../../services/settlement/settlement.service';
import { useShell } from '../../context/ShellContext';
import { useModalSubmit } from './useModalSubmit';

export interface SettleModalProps {
  separationId: string;
  holder: string;
  lastDay: string | null;
  openBalanceMinor: string;
  currency: string;
}

export function SettleModal({
  separationId,
  holder,
  lastDay,
  openBalanceMinor,
  currency,
}: SettleModalProps) {
  const { closeModal } = useShell();
  const [action, setAction] = useState<'settle' | 'waive'>('settle');
  const [reason, setReason] = useState('');

  const isWaive = action === 'waive';

  const { submitting, run } = useModalSubmit({
    validate: () =>
      isWaive && !reason.trim()
        ? 'Waiving a settlement requires a reason. It stays on the record.'
        : null,
    submit: () => resolveSettlement(separationId, action, isWaive ? reason.trim() : undefined),
    title: isWaive ? 'Settlement waived' : 'Settled',
    fallback: isWaive
      ? 'The reason has been recorded against the separation.'
      : 'The assignment has been closed out.',
  });

  return (
    <Modal
      visible
      title={`Settle ${holder}`}
      hint="Close out what they are still holding before their last day."
      onClose={closeModal}
      primaryLabel={isWaive ? 'Waive it' : 'Settle now'}
      onPrimary={run}
      primaryLoading={submitting}
    >
      <View style={styles.box}>
        <Text style={styles.boxLabel}>Still holding</Text>
        <Text style={styles.boxValue}>{format(openBalanceMinor, currency)}</Text>
        <Text style={styles.boxMeta}>Last day {date(lastDay)}</Text>
      </View>

      <ChipRow
        label="What to do"
        required
        options={[
          { value: 'settle', label: 'Settle' },
          { value: 'waive', label: 'Waive' },
        ]}
        value={action}
        onChange={setAction}
      />

      {isWaive && (
        <Textarea
          label="Reason"
          required
          hint="Why this balance will not be recovered."
          value={reason}
          onChangeText={setReason}
          placeholder="Agreed write-off, approved by the CFO"
          maxLength={1000}
          rows={4}
        />
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  box: {
    padding: 13,
    backgroundColor: neutral.surface2,
    borderWidth: 1,
    borderColor: neutral.border,
    borderRadius: radii.box,
    gap: 3,
  },
  boxLabel: { ...sans('400'), fontSize: fontSize.meta, color: neutral.grey600 },
  boxValue: {
    ...sans('600'),
    fontSize: fontSize.md,
    lineHeight: leading(fontSize.md),
    color: neutral.ink,
  },
  boxMeta: { ...sans('400'), fontSize: fontSize.meta, color: neutral.grey600 },
});

export default SettleModal;
